// Экран 3 из ТЗ раздел 3.3: моя подписка (тариф, статус, устройства). Устройства (экран 4,
// привязка/отвязка/ссылки на установку) объединены СЮДА ЖЕ по запросу пользователя 2026-08-20 —
// тематически про одну подписку, отдельные вкладки/разделы были лишними при узком нижнем таббаре.
//
// Полноценное управление КАЖДЫМ экземпляром подписки отдельно (2026-08-20, повторный запрос
// пользователя после первой read-only версии списка) — экран больше не различает "текущую"
// подписку и "остальные": единственный источник данных — `fetchAllSubscriptions()`
// (`GET /miniapp/subscriptions`), список ВСЕХ активных подписок, сгруппированных по тарифу.
// Каждый экземпляр — своя карточка (`SubscriptionCard`) с собственными устройствами,
// привязкой/отвязкой — не только "самый свежий". `subscription_id` теперь везде явный
// (attachDevice в api/client.ts), backend проверяет владение через `get_owned_subscription`
// (см. docs/OPEN_QUESTIONS.md про закрытый пробел).
//
// Автопродление (было здесь до 2026-08-20) переехало в раздел "Управление" (ChangePlanScreen.tsx,
// переименован из "Смена тарифа") — по запросу пользователя: тот раздел теперь общий хаб действий
// над подпиской (апгрейд + запланированное понижение + автопродление), этот экран — только про
// устройства.
//
// subscription.bundle_partner — вторая половина пары "Расширенный" (Mobile+Premium) КОНКРЕТНОГО
// экземпляра. Привязка/отвязка устройств на этой половине сознательно не поддержана — текущий
// AV-Desk клиент адресует станцию через `Subscription.av_license_id` анкорной половины (см.
// app/api/routes/miniapp/devices.py); скачивание дистрибутива — read-only действие, ЕСТЬ и для неё.
//
// Уточнения пользователя 2026-08-20 (раунды правок раздела "Устройства", предшествовавшие этому):
// - Онлайн/офлайн — только цветной кружок перед именем (зелёный/красный), текст — в title-атрибуте.
// - Кружок + имя + кнопка "Скачать" — один ряд (flex).
// - Сообщение "Достигнут лимит устройств тарифа" убрано без замены текстом.
// - Приписка "все дистрибутивы — архитектуры x64" — везде, где показываются ссылки на установку.
// - Сноска "* Dr.Web Расширенный — это..." — теперь ОДНА на весь экран, в самом низу (не по
//   карточке), звёздочка у названия тарифа ссылается на неё.
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  attachDevice,
  detachDevice,
  fetchAllSubscriptions,
  fetchDeviceInstallPackages,
  type Device,
  type InstallPackage,
  type Subscription,
  type SubscriptionSummary,
} from "../api/client";

const STATUS_KEYS = {
  active: "status_active",
  expiring: "status_expiring",
  expired: "status_expired",
  cancelled: "status_cancelled",
} as const;

// Человекочитаемые подписи для кодов ОС от AV-Desk — тот же список, что и в
// app/bot/formatting.py::_OS_LABELS (backend), продублирован здесь: фронт не может импортировать
// backend-код, а список маленький и стабильный (коды ОС определяет AV-Desk, не мы).
const OS_LABELS: Record<string, string> = {
  windows: "Windows",
  osx: "macOS",
  linux: "Linux",
  android: "Android",
  android4: "Android",
};

function osLabel(os: string): string {
  return OS_LABELS[os] ?? os;
}

function renderPackages(t: (key: string) => string, packages: InstallPackage[]) {
  // Приписка "x64" относится только к дистрибутивам для компьютера (Windows/macOS/Linux) — по
  // замечанию пользователя 2026-08-20 не должна показываться у чисто Android-списка (устройство
  // на "Мобильной" стороне пары "Расширенный" или обычного мобильного тарифа).
  const hasComputerPackage = packages.some((pkg) => !pkg.os.startsWith("android"));
  return packages.length === 0 ? (
    <p>{t("devices_links_pending")}</p>
  ) : (
    <>
      {hasComputerPackage && <p className="packages-note">{t("devices_packages_x64_note")}</p>}
      <ul>
        {packages.map((pkg) => (
          <li key={pkg.os}>
            <a href={pkg.url}>{osLabel(pkg.os)}</a>
          </li>
        ))}
      </ul>
    </>
  );
}

interface SubscriptionCardProps {
  subscription: Subscription;
  index: number;
  total: number;
  onChanged: () => void;
}

// Одна карточка = один экземпляр подписки, полностью самостоятельная (свой attach-form, свои
// busy-состояния устройств) — не делит состояние с другими карточками, даже если это два
// экземпляра одного тарифа. `onChanged` — просит родителя перечитать весь список после любого
// изменения (привязка/отвязка), а не хранит локально рассинхронизирующуюся копию.
function SubscriptionCard({ subscription, index, total, onChanged }: SubscriptionCardProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-US" : "ru-RU";

  const [name, setName] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [newPackages, setNewPackages] = useState<InstallPackage[] | null>(null);
  const [packagesByDevice, setPackagesByDevice] = useState<Record<number, InstallPackage[]>>({});
  const [busyDeviceId, setBusyDeviceId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAttach = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setAttaching(true);
    setError(null);
    setNewPackages(null);
    attachDevice(trimmed, subscription.id)
      .then(({ install_packages }) => {
        setName("");
        setNewPackages(install_packages);
        onChanged();
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setAttaching(false));
  };

  const handleShowLinks = (deviceId: number) => {
    setBusyDeviceId(deviceId);
    fetchDeviceInstallPackages(deviceId)
      .then((packages) => setPackagesByDevice((prev) => ({ ...prev, [deviceId]: packages })))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setBusyDeviceId(null));
  };

  const handleDetach = (deviceId: number) => {
    setBusyDeviceId(deviceId);
    setError(null);
    detachDevice(deviceId)
      .then(() => onChanged())
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setBusyDeviceId(null));
  };

  const renderDeviceList = (devices: Device[], options: { readOnly: boolean }) => (
    <ul>
      {devices.map((device) => (
        <li key={device.id}>
          <div className="device-row">
            <span
              className={`status-dot ${device.is_online ? "online" : "offline"}`}
              title={device.is_online ? t("devices_status_online") : t("devices_status_offline")}
            />
            <span className="device-name">
              {device.name}
              {device.is_primary ? ` — ${t("devices_primary")}` : ""}
            </span>
            <button
              type="button"
              onClick={() => handleShowLinks(device.id)}
              disabled={busyDeviceId === device.id}
            >
              {t("devices_show_links")}
            </button>
          </div>
          {!options.readOnly && !device.is_primary && (
            <button
              type="button"
              onClick={() => handleDetach(device.id)}
              disabled={busyDeviceId === device.id}
            >
              {t("devices_detach")}
            </button>
          )}
          {packagesByDevice[device.id] && renderPackages(t, packagesByDevice[device.id])}
        </li>
      ))}
    </ul>
  );

  const renderPartnerBlock = (partner: SubscriptionSummary) => (
    <div>
      <h4>{t("devices_group_computer")}</h4>
      {partner.devices.length === 0 ? (
        <p>{t("devices_empty")}</p>
      ) : (
        renderDeviceList(partner.devices, { readOnly: true })
      )}
    </div>
  );

  return (
    <li className="subscription-card">
      {total > 1 && (
        <p>
          <strong>#{index + 1}</strong>
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      <p>
        {t("subscription_status")}: {t(STATUS_KEYS[subscription.status])}
      </p>
      <p>
        {t("subscription_expires_at")}:{" "}
        {new Date(subscription.expires_at).toLocaleDateString(dateLocale)}
      </p>

      <h3>{t("devices_title")}</h3>
      {subscription.bundle_partner && <h4>{t("devices_group_mobile")}</h4>}
      {subscription.devices.length === 0 && <p>{t("devices_empty")}</p>}
      {renderDeviceList(subscription.devices, { readOnly: false })}

      {subscription.devices.length < subscription.device_limit && (
        <form onSubmit={handleAttach}>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("devices_name_placeholder")}
          />
          <button type="submit" disabled={attaching || !name.trim()}>
            {attaching ? t("loading") : t("devices_attach")}
          </button>
        </form>
      )}

      {newPackages && (
        <div>
          <p>{t("devices_new_links_title")}</p>
          {renderPackages(t, newPackages)}
        </div>
      )}

      {subscription.bundle_partner && renderPartnerBlock(subscription.bundle_partner)}
    </li>
  );
}

export function MySubscriptionScreen() {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<Subscription[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    fetchAllSubscriptions()
      .then(setSubscriptions)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)));

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh пересоздаётся каждый рендер, нужен только при монтировании
  }, []);

  const groups = new Map<number, Subscription[]>();
  for (const sub of subscriptions ?? []) {
    const group = groups.get(sub.plan.id);
    if (group) {
      group.push(sub);
    } else {
      groups.set(sub.plan.id, [sub]);
    }
  }
  const hasBundle = (subscriptions ?? []).some((s) => s.plan.is_bundle);

  return (
    <div>
      <h1>{t("subscription_title")}</h1>
      {error && <p role="alert">{error}</p>}
      {!error && subscriptions === undefined && <p>{t("loading")}</p>}
      {!error && subscriptions !== undefined && subscriptions.length === 0 && (
        <p>{t("subscription_none")}</p>
      )}
      {[...groups.values()].map((group) => (
        <div key={group[0].plan.id}>
          <h2>
            {group[0].plan.title}
            {group[0].plan.is_bundle ? "*" : ""}{" "}
            {t("subscriptions_count", { count: group.length })}
          </h2>
          <ul>
            {group.map((sub, index) => (
              <SubscriptionCard
                key={sub.id}
                subscription={sub}
                index={index}
                total={group.length}
                onChanged={refresh}
              />
            ))}
          </ul>
        </div>
      ))}

      {hasBundle && <p className="footnote">* {t("subscription_bundle_footnote")}</p>}
    </div>
  );
}
